import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { newIdempotencyKey } from '../client/idempotency';
/**
 * The two member-facing account surfaces that actually exist (spec §14).
 *
 * Everything else on the design's Profile — crew, co-parent, destinations,
 * avatar, change email, change password, privacy — has no route at all. Those
 * are named in `Backend_Gap_Analysis.md` §2 and requested in round 3 §6–§9;
 * nothing is stubbed here, because a hook against a route that does not exist
 * is a lie that typechecks.
 */
export const NotificationChannel = z.enum(['PUSH', 'EMAIL', 'BOTH']);
/** One person's setting, resolved against the catalogue default. */
export const NotificationSetting = z.object({
    eventKey: z.string(),
    channel: NotificationChannel,
    deliveryMode: z.string(),
    enabled: z.boolean(),
    /**
     * False for a MANDATORY event — there is no choice to offer.
     *
     * The control is omitted rather than disabled: a greyed toggle invites
     * somebody to try, and being refused is a worse answer than never being
     * asked. Security and billing notices are the obvious members.
     */
    canDisable: z.boolean(),
    subject: z.string(),
});
const SettingsList = z.object({ items: z.array(NotificationSetting) });
export function useNotificationSettings(http) {
    return useQuery({
        queryKey: ['notifications', 'settings'],
        queryFn: () => http.get('/notifications/settings', { schema: SettingsList }),
    });
}
export function useSetNotificationPreference(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ eventKey, enabled }) => http.post('/notifications/settings', { eventKey, enabled }, { schema: SettingsList }),
        // The write returns the whole list, so it replaces the cache rather than
        // triggering a refetch for data we are already holding.
        onSuccess: (data) => queryClient.setQueryData(['notifications', 'settings'], data),
    });
}
export const SubmitFeedback = z.object({
    message: z.string().min(1).max(5000),
    rating: z.number().int().min(1).max(5).optional(),
});
/**
 * Share feedback — open to any authenticated member.
 *
 * The locale is recorded server-side at submission, because a reply has to be
 * written in the language the member was using and asking afterwards is not
 * possible. Nothing to send from here; it reads the request.
 */
export function useSubmitFeedback(http) {
    return useMutation({
        mutationFn: (input) => http.post('/feedback', SubmitFeedback.parse(input), {
            schema: z.object({ id: z.string().uuid() }),
            idempotencyKey: newIdempotencyKey(),
        }),
    });
}
//# sourceMappingURL=account.js.map